import { Component, EventEmitter, Input, Output } from '@angular/core';
import { isFileRuleVisible } from 'src/app/utils/file-rule-visibility';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { File } from '../../model/file';
import { SubmissionStatus } from '../../enum/submission.status';
import { SubmissionsService } from '../../service/submissions.service';
import { FilesService } from '../../service/files.service';
import { InfoService } from '../../service/info.service';
import { NotificationService } from '../../service/notification.service';
import { PreferencesService } from '../../service/preferences.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

// Card "novo" da submissão — extraído de Minhas submissões para ser reutilizado
// em Minhas submissões, busca global (autor), busca dentro do evento (admin) e
// Submissões da admin do evento (TPC chair). Coluna de arquivos (`showFiles`) e
// permissão de upload/delete (`canEditFiles`) são controladas via inputs para
// adaptar o card ao contexto (autor vs. busca).
@Component({
  selector: 'app-submission-card-new',
  templateUrl: './submission-card-new.component.html',
  styleUrls: ['./submission-card-new.component.scss'],
})
export class SubmissionCardNewComponent {
  @Input() submission: any;
  // Mostra a coluna direita "Arquivos". Default true (caso de Minhas submissões
  // e admin do evento). Buscas heterogêneas geralmente passam false.
  @Input() showFiles = true;
  // Habilita upload/delete na coluna de arquivos. Quando false, a pill renderiza
  // apenas como visualização. Em "Minhas submissões", o card decide sozinho
  // (autor da submissão); em outros contextos, o consumidor força false.
  @Input() allowFileActions = true;
  // O checkbox de seleção aparece por DEFAULT (parte do componente). Consumidor
  // que NÃO quer a coluna de seleção passa [selectable]="false" pra escondê-la.
  @Input() selectable = true;
  // Estado controlado pelo pai: se o card está selecionado. Não persistido
  // localmente — o pai mantém o Set<id> e responde a (selectionChange).
  @Input() selected = false;
  // Preferência de bg-colored que o card lê. Default = a do usuário/chair
  // (Minhas submissões). Páginas alternativas podem passar chaves próprias
  // pra ter toggle independente (ex.: "Sessões que coordeno" usa
  // `statusColorAsBackgroundColorSessions`).
  @Input() colorPreferenceKey: string = 'statusColorAsBackgroundColor';
  // Renderiza a coluna "Apresentação" (rádios tri-estado no-show) quando
  // `event.noShowEnable` está ligado no evento. Consumer opt-in (default
  // false) — só páginas onde o usuário TEM permissão pra editar devem
  // passar true (ex.: "Sessões que coordeno"). Não exibir pro autor.
  @Input() allowNoShowEdit = false;

  @Output() submissionUpdated = new EventEmitter<any>();
  @Output() selectionChange = new EventEmitter<boolean>();

  // Forms de upload por submissão (lazy) — um por id.
  private uploadForms: Map<number, FormGroup> = new Map();

  constructor(
    public preferencesService: PreferencesService,
    private submissionService: SubmissionsService,
    private filesService: FilesService,
    private infoService: InfoService,
    private notificationService: NotificationService,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {}

  public getSubmissionStatusIcon(status: string) {
    return SubmissionStatus.icon(status as SubmissionStatus);
  }

  public getTrackFiles(submission: any): any[] {
    const track = submission?.track;
    const files = track?.trackFiles || [];
    // Backend não emite `track` no TrackFileSerializer, mas o
    // `submissionsService.submitFiles(...)` faz `track.trackFiles.find(...)`
    // usando `rule.track`. Sem esse relink o upload silencia. Só amarra 1x
    // (idempotente).
    for (const tf of files) {
      if (track && !tf.track) { tf.track = track; }
    }
    // Filtra por PAPEL. Antes devolvia todas as regras da trilha: revisor e
    // coordenador de sessão viam a label de arquivos marcados como invisíveis
    // para eles e, como o backend corretamente não envia o arquivo, o pill
    // exibia "arquivo ausente" (issue #2381). Mesma decisão do
    // `file-rules-card`, agora num helper só.
    const uid = this.infoService.user?.id;
    return files.filter((tf: any) => isFileRuleVisible(tf, {
      isAuthor: !!submission?.isAuthor?.(uid),
      // O card da lista não tem contexto de chair — quem administra o evento
      // acessa pela área do chair, que usa o `file-rules-card`.
      isChair: false,
      isReviewer: !!submission?.flagReviewer,
      isSessionChair: !!submission?.isSessionChair?.(uid),
    }));
  }

  public findUploadedFile(submission: any, trackFile: any): any | null {
    if (!submission?.files || !trackFile) { return null; }
    return submission.files.find((f: any) => f?.trackFile?.id === trackFile.id) || null;
  }

  public getUploadForm(submission: any): FormGroup {
    if (!this.uploadForms.has(submission.id)) {
      // `app-file-upload` lê `formGroupController.controls['files']` no
      // ngOnInit e usa `stateCtrl.value[formName] = files` no change do
      // <input type="file">. Sem esse control inicializado (valor `{}`),
      // o handler quebra silenciosamente ao escolher arquivo e o upload
      // não roda. Mesmo shape usado em `FileUploadViewDirective:initUploadForm`.
      this.uploadForms.set(submission.id, this.fb.group({ files: [{}] }));
    }
    return this.uploadForms.get(submission.id);
  }

  public isUserAuthor(submission: any): boolean {
    if (!this.allowFileActions) { return false; }
    const uid = this.infoService.user?.id;
    if (!uid || !submission?.authors) { return false; }
    return submission.authors.some((a: any) => a?.user?.id === uid);
  }

  public fileUploadComplete(submission: any): void {
    this.submissionService.getSubmission(submission.id, true).subscribe(updated => {
      this.mergeUpdatedSubmission(submission, updated);
      // Transição PENDING → ACTIVE quando todos os arquivos obrigatórios
      // estão presentes. Mesma lógica do `FileUploadViewDirective.verify
      // AndUpdateStatus`; sem isto, o card fica em "Rascunho" mesmo com o
      // upload concluído.
      this.maybePromoteToActive(submission);
      this.submissionUpdated.emit(submission);
    });
  }

  private maybePromoteToActive(submission: any): void {
    if (!submission || String(submission.status).toUpperCase() !== SubmissionStatus.PENDING) { return; }
    const trackFiles = submission?.track?.trackFiles || [];
    const submittedTrackFiles = (submission?.files || []).map((f: any) => f.trackFile).filter(Boolean);
    if (!this.filesService.requiredFilesSubmitted(trackFiles, submittedTrackFiles)) { return; }
    this.submissionService.editSubmission({ status: SubmissionStatus.ACTIVE }, submission.id).subscribe(
      () => {
        submission.status = SubmissionStatus.ACTIVE;
        this.submissionUpdated.emit(submission);
      }
    );
  }

  // ── No-show tri-estado ──────────────────────────────────────────────
  // Gate de renderização da coluna: coluna só aparece se o EVENTO tem
  // no-show ligado E o consumer opt-in via `allowNoShowEdit`.
  public get showNoShowColumn(): boolean {
    return this.allowNoShowEdit && !!this.submission?.event?.noShowEnable;
  }

  // `null/undefined` = Indefinido, `false` = Apresentado, `true` = No-show.
  public isNoShowValue(value: boolean | null): boolean {
    const current = this.submission?.noShow;
    if (value === null) { return current === null || current === undefined; }
    return current === value;
  }

  // Feedback visual: spinner enquanto grava, check por ~1.2s após ok.
  public noShowSaving = false;
  public noShowSaved = false;
  private noShowSavedTimer?: any;

  // Gravação otimista: reflete no modelo local, chama o endpoint dedicado
  // (`update_submission_no_show`, aceita session chair), reverte em erro.
  public setNoShow(value: boolean | null): void {
    if (!this.submission || this.submission.noShow === value) { return; }
    const previous = this.submission.noShow;
    this.submission.noShow = value;
    this.noShowSaving = true;
    this.noShowSaved = false;
    if (this.noShowSavedTimer) { clearTimeout(this.noShowSavedTimer); }
    this.submissionService.editSubmissionNoShow(this.submission.id, value).subscribe(
      () => {
        this.noShowSaving = false;
        this.noShowSaved = true;
        this.noShowSavedTimer = setTimeout(() => { this.noShowSaved = false; }, 750);
        // Notifica o pai — session-chairings escuta pra atualizar o badge
        // da sidebar em tempo real (Indefinido → Apresentado/No-show
        // decrementa; caminho contrário incrementa).
        this.submissionUpdated.emit(this.submission);
      },
      () => {
        this.noShowSaving = false;
        this.submission.noShow = previous;
      }
    );
  }

  public deleteFile(file: File, submission: any): void {
    this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true, autoFocus: false,
      data: { title: 'messages.confirm-file-deletion', content: '' }
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) { return; }
      this.submissionService.deleteSubmissionFile(file, submission).subscribe(() => {
        this.submissionService.getSubmission(submission.id, true).subscribe(updated => {
          this.mergeUpdatedSubmission(submission, updated);
          this.notificationService.notify('messages.removed-file');
          this.submissionUpdated.emit(submission);
        });
      });
    });
  }

  // Merge SEGURO: preserva o `track` expandido (com trackFiles) que veio
  // do payload MINIMUM da lista — o `getSubmission` full retorna `track`
  // como PK crua (`SubmissionSerializer`), e um `Object.assign` puro faria
  // o card cair pra "Sem arquivos" porque `getTrackFiles` deixaria de
  // achar `submission.track.trackFiles`.
  private mergeUpdatedSubmission(target: any, updated: any): void {
    if (!updated) { return; }
    const trackIsExpanded = typeof updated.track === 'object' && updated.track?.trackFiles;
    for (const key of Object.keys(updated)) {
      if (key === 'track' && !trackIsExpanded) { continue; }
      target[key] = updated[key];
    }
  }
}
